import React, { useState, useEffect, useMemo } from "react";
import { database } from "../../firebase";
import { ref, get } from "firebase/database";
import { useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import {
  Alert,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SideBarForRole7 from "../sidebar/SideBarForRole7";
import NavbarForRole7 from "../navbar/NavbarForRole7";
import EmptyState from "../ui/EmptyState";
import {
  fetchHeartbeatForAll,
  subscribeToHeartbeatStatuses,
} from "../../utils/watchHeartbeat";
import { formatRelative } from "../../utils/format";
import SearchIcon from "@mui/icons-material/Search";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import WatchOutlinedIcon from "@mui/icons-material/WatchOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import "./selectWatch.scss";

const WatchesImages = [
    { id: "SM-L305F", url: "/Images/SM-L305F.png" },
    { id: "SM-L310", url: "/Images/SM-L310.png" },
    { id: "SM-L315F", url: "/Images/SM-L315F.png" },
    { id: "SM-L705F", url: "/Images/SM-L705F.png" },
    { id: "SM-R910", url: "/Images/SM-R910.png" },
    { id: "SM-R915F", url: "/Images/SM-R915F.png" },
    { id: "SM-R930", url: "/Images/SM-R930.png" },
    { id: "SM-R935F", url: "/Images/SM-R935F.png" },
    { id: "SM-R940", url: "/Images/SM-R940.png" },
    { id: "SM-R945F", url: "/Images/SM-R945F.png" },

    // extra art you added
    { id: "SM-G556B", url: "/Images/SM-G556B.png" },
    { id: "SM-G556BU", url: "/Images/SM-G556BU.png" },
    { id: "SM-G556U", url: "/Images/SM-G556U.png" },
    { id: "SM-G556U1", url: "/Images/SM-G556U1.png" },
    { id: "SM-G736B", url: "/Images/SM-G736B.png" },
    { id: "SM-G736U", url: "/Images/SM-G736U.png" },
    { id: "SM-G736U1", url: "/Images/SM-G736U1.png" },
    { id: "SM-G766B", url: "/Images/SM-G766B.png" },
    { id: "SM-G766BU1", url: "/Images/SM-R940.png" },

    { id: "SM-L320", url: "/Images/SM-L320.png" },
    { id: "SM-L325", url: "/Images/SM-L325.png" },
    { id: "SM-L325U", url: "/Images/SM-L325U.png" },
    { id: "SM-L330", url: "/Images/SM-L330.png" },
    { id: "SM-L335", url: "/Images/SM-L335.png" },
    { id: "SM-L335F", url: "/Images/SM-L335F.png" },
    { id: "SM-L335U", url: "/Images/SM-L335U.png" },
    { id: "SM-L500", url: "/Images/SM-L500.png" },
    { id: "SM-L505F", url: "/Images/SM-L505F.png" },
    { id: "SM-L705F", url: "/Images/SM-L705F.png" },
];

const normalizeId = (value) => (value ?? "").toString();

const CustomUrlComponent = () => {
  const { currentUser } = useContext(AuthContext);
  const [allWatches, setAllWatches] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("all");
  const [selectedLocation, setSelectedLocation] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [heartbeatStatuses, setHeartbeatStatuses] = useState({});
  const [statusError, setStatusError] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);

  const navigate = useNavigate();

  const userRole = currentUser?.role ?? null;
  const userCompanyId = normalizeId(currentUser?.companyId);
  const userLocationId = normalizeId(currentUser?.locationId);

  const heroDescription = useMemo(() => {
    if (!currentUser) {
      return "Select a watch to dive into its health metrics.";
    }

    if (userRole === 1) {
      return "Filter by company and location to explore the entire Watch Guardian fleet.";
    }

    if (userRole === 2) {
      return `Review every device across ${currentUser?.companyName || "your company"}.`;
    }

    return `You are viewing devices assigned to ${currentUser?.locationName || "your location"}.`;
  }, [currentUser, userRole]);

  useEffect(() => {
    if (!currentUser) {
      setAllWatches([]);
      return;
    }

    const watchesRef = ref(database, "Watches/");
    get(watchesRef)
      .then((snapshot) => {
        if (snapshot.exists()) {
          const watchesData = snapshot.val();
          const normalizedWatches = Object.values(watchesData)
            .filter(
              (watch) =>
                watch?.watchAssist &&
                watch?.watchHealth === true
            )
            .map((watch) => ({
              ...watch,
              companyId: normalizeId(watch?.companyId),
              locationId: normalizeId(watch?.locationId),
            }));

          setAllWatches(normalizedWatches);
        } else {
          setAllWatches([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching watches: ", error);
        setAllWatches([]);
      });
  }, [currentUser]);

  const companyOptions = useMemo(() => {
    if (userRole !== 1) return [];
    const map = new Map();
    allWatches.forEach((watch) => {
      const id = watch?.companyId;
      if (!id) return;
      if (!map.has(id)) {
        map.set(id, watch?.companyName || id);
      }
    });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [allWatches, userRole]);

  const locationOptions = useMemo(() => {
    let source = [];

    if (!userRole && userRole !== 0) {
      return [];
    }

    if (userRole === 1) {
      source =
        selectedCompany === "all"
          ? allWatches
          : allWatches.filter((watch) => watch?.companyId === selectedCompany);
    } else if (userRole === 2) {
      source = allWatches.filter((watch) => watch?.companyId === userCompanyId);
    } else {
      source = allWatches.filter(
        (watch) =>
          watch?.companyId === userCompanyId &&
          watch?.locationId === userLocationId
      );
    }

    const map = new Map();
    source.forEach((watch) => {
      const id = watch?.locationId;
      if (!id) return;
      if (!map.has(id)) {
        map.set(id, watch?.locationName || id);
      }
    });

    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [allWatches, selectedCompany, userCompanyId, userLocationId, userRole]);

  useEffect(() => {
    if (selectedLocation === "all") return;
    const hasLocation = locationOptions.some((option) => option.id === selectedLocation);
    if (!hasLocation) {
      setSelectedLocation("all");
    }
  }, [locationOptions, selectedLocation]);

  const watchesForRole = useMemo(() => {
    if (!currentUser) return [];

    let accessible = allWatches;

    if (userRole === 2) {
      accessible = accessible.filter(
        (watch) => watch?.companyId === userCompanyId
      );
    } else if (userRole !== 1) {
      accessible = accessible.filter(
        (watch) =>
          watch?.companyId === userCompanyId &&
          watch?.locationId === userLocationId
      );
    }

    if (userRole === 1) {
      if (selectedCompany !== "all") {
        accessible = accessible.filter(
          (watch) => watch?.companyId === selectedCompany
        );
      }

      if (selectedLocation !== "all") {
        accessible = accessible.filter(
          (watch) => watch?.locationId === selectedLocation
        );
      }
    } else if (userRole === 2) {
      if (selectedLocation !== "all") {
        accessible = accessible.filter(
          (watch) => watch?.locationId === selectedLocation
        );
      }
    }

    return accessible;
  }, [
    allWatches,
    currentUser,
    selectedCompany,
    selectedLocation,
    userCompanyId,
    userLocationId,
    userRole,
  ]);

  const visibleWatches = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return watchesForRole;
    }

    return watchesForRole.filter((watch) => {
      const haystack = [
        watch?.watchId,
        watch?.watchDisplayName,
        watch?.deviceName,
        watch?.companyName,
        watch?.locationName,
      ];
      return haystack.some((value) =>
        value?.toString().toLowerCase().includes(query)
      );
    });
  }, [searchQuery, watchesForRole]);

  const watchIds = useMemo(() => {
    if (!watchesForRole?.length) return [];
    const unique = new Set();
    watchesForRole.forEach((watch) => {
      if (watch?.watchId) unique.add(watch.watchId);
    });
    return Array.from(unique);
  }, [watchesForRole]);

  useEffect(() => {
    if (!watchIds.length) {
      setHeartbeatStatuses({});
      setStatusLoading(false);
      setStatusError(null);
      return undefined;
    }

    let cancelled = false;

    setHeartbeatStatuses((prev) => {
      const next = {};
      watchIds.forEach((id) => {
        if (prev[id]) {
          next[id] = prev[id];
        }
      });
      return next;
    });

    const load = async () => {
      setStatusLoading(true);
      try {
        const statuses = await fetchHeartbeatForAll(watchIds);
        if (cancelled) return;
        setHeartbeatStatuses((prev) => ({ ...prev, ...statuses }));
        setStatusError(null);
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to load heartbeat statuses", error);
        setStatusError(error);
      } finally {
        if (!cancelled) {
          setStatusLoading(false);
        }
      }
    };

    load();

    const unsubscribe = subscribeToHeartbeatStatuses(
      watchIds,
      (watchId, status) => {
        setHeartbeatStatuses((prev) => ({
          ...prev,
          [watchId]: status,
        }));
      }
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [watchIds]);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleCompanyChange = (event) => {
    setSelectedCompany(event.target.value);
    setSelectedLocation("all");
  };

  const handleLocationChange = (event) => {
    setSelectedLocation(event.target.value);
  };

  const visibleWatchesWithStatus = useMemo(() => {
    const watchesWithStatus = visibleWatches.map((watch) => ({
      ...watch,
      heartbeatStatus: heartbeatStatuses[watch.watchId] ?? null,
    }));

    return watchesWithStatus.sort((a, b) => {
      const aOnline = a.heartbeatStatus?.isOnline ? 1 : 0;
      const bOnline = b.heartbeatStatus?.isOnline ? 1 : 0;
      if (aOnline !== bOnline) {
        return bOnline - aOnline;
      }

      const aLastOnline = a.heartbeatStatus?.lastOnlineMs ?? 0;
      const bLastOnline = b.heartbeatStatus?.lastOnlineMs ?? 0;
      if (aLastOnline !== bLastOnline) {
        return bLastOnline - aLastOnline;
      }

      const aName = (a.watchDisplayName || a.watchId || "").toString().toLowerCase();
      const bName = (b.watchDisplayName || b.watchId || "").toString().toLowerCase();
      return aName.localeCompare(bName);
    });
  }, [heartbeatStatuses, visibleWatches]);

  const handleSelectWatch = (watch) => {
    if (!watch?.watchId) return;
    const destinationCompanyId = watch.companyId || userCompanyId;
    const destinationLocationId = watch.locationId || userLocationId;

    navigate(
      `/${destinationCompanyId}/${destinationLocationId}/dashboard/${watch.watchId}`
    );
  };


  return (
    <div className="wg-shell">
      <SideBarForRole7 />

      <div className="wg-main">
        <NavbarForRole7 />

        <main className="wg-content">
          <div className="wg-page-head">
            <div className="wg-page-head__text">
              <p className="wg-eyebrow">Fleet</p>
              <h1 className="wg-title">Select a watch</h1>
              <p className="wg-subtitle">{heroDescription}</p>
            </div>
            <div className="wg-page-head__actions">
              <span className="wg-chip">
                {visibleWatchesWithStatus.length} of {watchesForRole.length} shown
              </span>
              {statusLoading && <span className="wg-chip">Refreshing status…</span>}
            </div>
          </div>

          <section className="wg-card">
            <div className="wg-card__body wg-card__body--tight">
              <div className="select-watch__filters">
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by ID, device, company or location"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />

                {userRole === 1 && (
                  <FormControl size="small">
                    <InputLabel>Company</InputLabel>
                    <Select
                      label="Company"
                      value={selectedCompany}
                      onChange={handleCompanyChange}
                    >
                      <MenuItem value="all">All companies</MenuItem>
                      {companyOptions.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {(userRole === 1 || userRole === 2) && (
                  <FormControl size="small">
                    <InputLabel>Location</InputLabel>
                    <Select
                      label="Location"
                      value={selectedLocation}
                      onChange={handleLocationChange}
                    >
                      <MenuItem value="all">
                        {userRole === 1 ? "All locations" : "All company locations"}
                      </MenuItem>
                      {locationOptions.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </div>
            </div>
          </section>

          {statusError && (
            <Alert severity="warning">
              Unable to refresh watch statuses. Showing the last known information.
            </Alert>
          )}

          {visibleWatchesWithStatus.length === 0 ? (
            <EmptyState
              icon={<WatchOutlinedIcon />}
              title="No watches match your filters"
              copy="Adjust the search or filter selections to see the available devices."
            />
          ) : (
            <div className="select-watch__grid">
              {visibleWatchesWithStatus.map((watch) => {
                const status = watch.heartbeatStatus;
                const known = status != null;
                const isOnline = status?.isOnline ?? false;

                const lastOnlineMs = status?.lastOnline
                  ? new Date(status.lastOnline).getTime()
                  : null;
                const lastOnlineLabel =
                  lastOnlineMs && !Number.isNaN(lastOnlineMs)
                    ? formatRelative(lastOnlineMs)
                    : null;

                const artwork = WatchesImages.find(
                  (image) => image.id === watch.deviceName
                )?.url;

                // Status reads as dot + label, never colour alone.
                const statusChip = !known
                  ? {
                      className: "wg-chip",
                      label: statusLoading ? "Updating…" : "Unknown",
                    }
                  : isOnline
                  ? { className: "wg-chip wg-chip--good", label: "Online" }
                  : { className: "wg-chip wg-chip--critical", label: "Offline" };

                return (
                  <button
                    type="button"
                    key={watch.watchId}
                    className="wg-card wg-card--interactive watch-tile"
                    onClick={() => handleSelectWatch(watch)}
                  >
                    <span className="watch-tile__top">
                      <span className="watch-tile__art">
                        {artwork ? (
                          <img src={artwork} alt="" />
                        ) : (
                          <span className="watch-tile__art-empty">No art</span>
                        )}
                      </span>

                      <span className="watch-tile__ident">
                        <span className="watch-tile__device">
                          {watch.deviceName || "Device"}
                        </span>
                        <span className="watch-tile__name">
                          {watch.watchDisplayName || watch.watchId}
                        </span>
                        <span className="watch-tile__id wg-mono">{watch.watchId}</span>
                      </span>

                      <span className={statusChip.className}>
                        <span
                          className={`wg-chip__dot ${
                            isOnline ? "wg-chip__dot--pulse" : ""
                          }`}
                          style={known ? undefined : { background: "var(--wg-ink-muted)" }}
                        />
                        {statusChip.label}
                      </span>
                    </span>

                    <span className="watch-tile__facts">
                      {userRole === 1 && (
                        <span className="watch-tile__fact">
                          <ApartmentOutlinedIcon />
                          <span>{watch.companyName || `Company ${watch.companyId}`}</span>
                        </span>
                      )}
                      <span className="watch-tile__fact">
                        <PlaceOutlinedIcon />
                        <span>
                          {watch.locationName || `Location ${watch.locationId}`}
                        </span>
                      </span>
                      {lastOnlineLabel && (
                        <span className="watch-tile__fact">
                          <WatchOutlinedIcon />
                          <span>Last online {lastOnlineLabel}</span>
                        </span>
                      )}
                    </span>

                    <span className="watch-tile__cta">
                      Open dashboard
                      <ArrowForwardIcon />
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default CustomUrlComponent;
